/* eslint-disable react-refresh/only-export-components */
import { useDirection } from "@radix-ui/react-direction";
import { CircleCheckBig, Info, Loader2, TriangleAlert } from "lucide-react";
import { useTheme } from "next-themes";
import { Toaster as Sonner } from "sonner";

type TToasterProps = React.ComponentProps<typeof Sonner>;

const Toaster = ({ ...props }: TToasterProps) => {
  const dir = useDirection();
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      dir={dir}
      theme={theme as TToasterProps["theme"]}
      position={dir === "rtl" ? "bottom-left" : "bottom-right"}
      // NOTE: NSAI-4221 — screens with a bottom-anchored composer (chat) set
      // --toaster-bottom-offset so toasts rest above it instead of on the
      // send button; everywhere else the sonner default (32px) applies.
      offset={{ bottom: "var(--toaster-bottom-offset, 32px)" }}
      className="toaster group"
      icons={{
        success: <CircleCheckBig className="size-4" />,
        error: <Info className="size-4" />,
        info: <Info className="size-4" />,
        warning: <TriangleAlert className="size-4" />,
        loading: <Loader2 className="size-4 animate-spin" />,
      }}
      closeButton={true}
      toastOptions={{
        classNames: {
          // NOTE: NSAI-4221 — toasts are click-through (pointer-events-none)
          // so an overlapping toast never swallows clicks meant for controls
          // beneath it (e.g. the chat send button); interactive children
          // re-enable pointer events below.
          toast:
            "pointer-events-none !items-start bg-toast-info text-toast-info-foreground !border-none data-[type=success]:bg-toast-success data-[type=success]:text-toast-success-foreground data-[type=error]:bg-toast-error data-[type=error]:text-toast-error-foreground data-[type=info]:bg-toast-info data-[type=info]:text-toast-info-foreground data-[type=warning]:bg-toast-warning data-[type=warning]:text-toast-warning-foreground data-[type=loading]:bg-toast-info data-[type=loading]:text-toast-info-foreground",
          icon: "mt-0.5",
          closeButton:
            "pointer-events-auto relative order-last translate-x-0 translate-y-0 !mt-0 self-start [&_svg]:size-4 !border-none !bg-transparent !text-current hover:!bg-transparent hover:opacity-70",
          actionButton: "pointer-events-auto",
          cancelButton: "pointer-events-auto",
          content: "grow min-w-0",
          title: "break-words line-clamp-3",
        },
      }}
      {...props}
    />
  );
};

export { toast } from "sonner";
export { Toaster };
